import { useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, FileBox, Target, Users, ShieldCheck, Zap 
} from "lucide-react";
import { cn } from "@/lib/utils";

export default function SobrePage() {
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains("dark");
    if (hadDark) {
      root.classList.remove("dark");
      root.classList.add("light");
    }
    return () => {
      if (hadDark) {
        root.classList.remove("light");
        root.classList.add("dark");
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EAF4FF] via-[#F5FAFF] to-[#E6F0FF] text-slate-900 font-sans selection:bg-blue-200 relative overflow-hidden pb-20">
      
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-32 size-[520px] rounded-full bg-[radial-gradient(circle_at_center,#33CCFF33,transparent_60%)] blur-3xl" />
        <div className="absolute top-[20%] -right-40 size-[600px] rounded-full bg-[radial-gradient(circle_at_center,#1A66CC22,transparent_60%)] blur-3xl" />
        <div className="absolute bottom-[-10%] left-[10%] size-[520px] rounded-full bg-[radial-gradient(circle_at_center,#A5C8FF44,transparent_65%)] blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto px-6 pt-12 relative z-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-bold text-blue-600 hover:text-blue-700 mb-8 group transition-all"
        >
          <div className="size-8 rounded-lg bg-white shadow-sm border border-blue-100 flex items-center justify-center group-hover:-translate-x-1 transition-transform">
            <ArrowLeft className="size-4" />
          </div>
          Voltar para o Início
        </Link>

        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-br from-[#33CCFF]/10 to-[#1A66CC]/5 rounded-[2.5rem] blur-2xl opacity-50" />
          
          <div className="relative bg-white/60 backdrop-blur-3xl rounded-[2rem] border border-white/80 p-8 lg:p-12 shadow-[0_30px_80px_-20px_rgba(26,102,204,0.15),inset_0_1px_1px_rgba(255,255,255,1)]">
            
            <header className="flex flex-col sm:flex-row sm:items-center gap-5 mb-12 pb-8 border-b border-blue-100/50">
              <div className="size-14 rounded-2xl bg-gradient-to-br from-[#33CCFF] to-[#1A66CC] grid place-items-center text-white shadow-lg shadow-blue-500/30">
                <Users className="size-7" />
              </div>
              <div>
                <h1 className="text-3xl font-black tracking-tight text-slate-900 mb-1">Sobre nós</h1>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                  Nossa Missão e Valores
                </p>
              </div>
            </header>

            <div className="prose prose-slate max-w-none space-y-12">
              <section className="space-y-6">
                <h2 className="text-2xl font-black text-slate-900 flex items-center gap-3 m-0">
                  <Target className="size-6 text-blue-600" />
                  Nossa Missão
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed italic">
                  "Democratizar o acesso à inteligência fiscal, oferecendo ferramentas de inspeção de alta performance que garantem privacidade absoluta através da tecnologia de processamento local."
                </p>
              </section>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white/40 p-6 rounded-2xl border border-white/60 shadow-sm transition-all hover:shadow-md">
                  <div className="size-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 mb-4">
                    <ShieldCheck className="size-6" />
                  </div>
                  <h3 className="text-lg font-black text-slate-900 mb-2">Privacidade Primeiro</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Acreditamos que seus dados fiscais não devem sair da sua máquina. Por isso, desenvolvemos uma arquitetura onde o processamento ocorre 100% no seu navegador.
                  </p>
                </div>

                <div className="bg-white/40 p-6 rounded-2xl border border-white/60 shadow-sm transition-all hover:shadow-md">
                  <div className="size-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 mb-4">
                    <Zap className="size-6" />
                  </div>
                  <h3 className="text-lg font-black text-slate-900 mb-2">Agilidade Sistêmica</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Transformamos XMLs complexos em insights acionáveis em segundos, eliminando a burocracia do processamento manual de documentos fiscais.
                  </p>
                </div>
              </div>

              <section className="space-y-6 pt-8 border-t border-blue-100/50">
                <h2 className="text-2xl font-black text-slate-900 m-0">A Ferramenta</h2>
                <p className="text-base text-slate-600 leading-relaxed">
                  O <strong>Extrator Fiscal</strong> nasceu da necessidade de contadores e analistas fiscais em validar grandes lotes de documentos sem comprometer a segurança da informação ou depender de softwares pesados e caros.
                </p>
                <div className="bg-slate-900 p-8 rounded-3xl text-white relative overflow-hidden">
                  <FileBox className="absolute -right-8 -bottom-8 size-40 opacity-10 rotate-12" />
                  <p className="text-sm leading-relaxed relative z-10 mb-0">
                    Somos uma ferramenta de TI (SaaS) focada em extração e estruturação de dados, desenhada para ser o braço direito do profissional da contabilidade moderna.
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
